import { createContext, useEffect, useState, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useGlobalContext } from "../globalContext";
import useStepFourValues from "./useStepFourValues";
import useStepFiveValues from "./useStepFiveValues";
import useStepSixValues from "./useStepSixValues";
import useStepSevenValues from "./useStepSevenValues";
import Agreement from "../../components/LegalForm/steps/agreement/agreement";
import Step2 from "../../components/LegalForm/steps/step2/index";
export const LegalFormStepsContext = createContext(null);

export function LegalFormStepsContextProvider({ children }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const {
    globalData: { status },
  } = useGlobalContext();
  const { stepFourValues, setStepFourValues } = useStepFourValues();
  const { stepFiveValues, setStepFiveValues } = useStepFiveValues();
  const { stepSixValues, setStepSixValues } = useStepSixValues();
  const { stepSevenValues, setStepSevenValues } = useStepSevenValues();

  const [redirectUrl, setRedirectUrl] = useState(null);
  const [files, setFiles] = useState([]);
  const [beneficialOwners, setBeneficialOwners] = useState(() => [
    crypto.randomUUID(),
  ]);

  const handleSetActiveStep = () => {
    if (
      history.location.pathname === "/Legal/step/2/ka" ||
      history.location.pathname === "/Legal/step/2/en"
    ) {
      return {
        id: 2,
        component: <Step2 />,
        route: `/Legal/step/2/${i18n.language}`,
      };
    } else {
      return {
        id: 1,
        component: <Agreement />,
        route: `/Legal/agreement/${i18n.language}`,
      };
    }
  };

  const [activeStep, setActiveStep] = useState(handleSetActiveStep());

  const steps = useMemo(
    () => [
      {
        id: 1,
        title: t("confirmationStep1"),
        disabled: true,
        route: `/Legal/agreement/${i18n.language}`,
      },
      {
        id: 2,
        title: t("conditions"),
        disabled: true,
        route: `/Legal/step/2/${i18n.language}`,
      },
      {
        id: 3,
        title: t("identificationStep2"),
        disabled: status !== "approved",
        route: `/Legal/Identomat/${i18n.language}`,
      },
      {
        id: 4,
        title: t("aboutTheOrganization"),
        disabled: status !== "approved",
        route: `/Legal/step/4`,
      },

      {
        id: 5,
        title: t("authorizedPerson"),
        disabled: status !== "approved",
        route: `/Legal/step/5`,
      },
      {
        id: 6,
        title: t("aboutServices"),
        disabled: status !== "approved",
        route: `/Legal/step/6`,
      },
      {
        id: 7,
        title: t("identityInformation6"),
        disabled: status !== "approved",
        route: `/Legal/step/7`,
      },
    ],
    [status, i18n.language, t]
  );

  const nextStep = () => {
    steps.forEach((step) => {
      if (step.id === activeStep.id + 1) {
        history.push(step.route);
      }
    });
  };

  const prevStep = () => {
    if (activeStep.id - 1 !== 2) {
      steps.forEach((step) => {
        if (step.id === activeStep.id - 1) {
          history.push(step.route);
        }
      });
    }
  };

  return (
    <LegalFormStepsContext.Provider
      value={{
        activeStep,
        nextStep,
        prevStep,
        redirectUrl,
        setRedirectUrl,
        steps,
        setActiveStep,
        stepFourValues,
        setStepFourValues,
        stepFiveValues,
        setStepFiveValues,
        stepSixValues,
        setStepSixValues,
        stepSevenValues,
        setStepSevenValues,
        files,
        setFiles,
        beneficialOwners,
        setBeneficialOwners,
      }}
    >
      {children}
    </LegalFormStepsContext.Provider>
  );
}

export function useLegalFormStepsContext() {
  const value = useContext(LegalFormStepsContext);
  if (!value) {
    throw new Error("LegalForm Steps context Provider is not defined");
  }
  return value;
}
